import { CommonModule } from '@angular/common';
import { Component, ViewChild, AfterViewInit, OnInit } from '@angular/core';
import { LeadService } from '../shared/service/leads.service';
import { DynamicFormComponent } from '../../../core/shared/dynamic-form/dynamic-form.component';
import { DynamicFieldConfig } from '../../../core/shared/dynamic-form/form-config.model';
import { ToastrService } from 'ngx-toastr';
import { Router, ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { BackButton } from '../../shared/buttons/back-button';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-demographic',
  imports: [CommonModule, DynamicFormComponent, FormsModule, BackButton,TranslatePipe],
  templateUrl: './demographic.html',
  styleUrl: './demographic.css',
})
export class Demographic implements OnInit, AfterViewInit {
  leadSeq: number | null = null;
  isEditMode = false;
  emailText: string = '';
  @ViewChild(DynamicFormComponent) dynamicForm!: DynamicFormComponent;

  showCreateFromEmail = false;
  selectedFile: File | null = null;
  isViewMode = false;
  continueAfterSave = false;

  get isRelationshipOfficer(): boolean {
    return (
      typeof localStorage !== 'undefined' &&
      (localStorage.getItem('user_role') || '').trim().toUpperCase() === 'LNLY_RO'
    );
  }

fields: DynamicFieldConfig[] = [];
buildFields(): void {
  this.fields = [
    {
      key: 'leadId',
      label: this.translate.instant('DEMOGRAPHIC.LEAD_ID'),
      type: 'text',
      disabled: true
    },
    {
      key: 'clientName',
      label: this.translate.instant('DEMOGRAPHIC.INSTITUTION_NAME'),
      type: 'text',
      // Letters, numbers, spaces and - & . / (e.g. "A&B Micro-Finance Co./Ltd").
      validations: { minLength: 3, maxLength: 300, required: true, pattern: '^[a-zA-Z0-9 .&/-]+$' },
      errorMessages: {
        pattern: 'DEMOGRAPHIC.INSTITUTION_NAME_PATTERN',
      }
    },
    {
      key: 'date',
      label: this.translate.instant('DEMOGRAPHIC.DATE'),
      type: 'date',
      validations: { required: true, noFuturDate: true }
    },
    {
      key: 'contactName',
      label: this.translate.instant('DEMOGRAPHIC.CONTACT_NAME'),
      type: 'text',
      validations: { minLength: 3, maxLength: 300, required: true, pattern: '^[a-zA-Z ]+$' },
      errorMessages: {
        pattern: 'DEMOGRAPHIC.ONLY_LETTERS',
      }
    },
    {
      key: 'phone',
      label: this.translate.instant('DEMOGRAPHIC.CONTACT_PHONE'),
      type: 'text',
      maxlength: 11,
      inputmode: 'numeric',
      validations: { required: true, phone: true }
    },
    {
      key: 'email',
      label: this.translate.instant('DEMOGRAPHIC.CONTACT_EMAIL'),
      type: 'email',
      validations: { required: true, email: true, knownEmailProviderDomain: true },
      errorMessages: {
        knownEmailProviderDomain: 'DEMOGRAPHIC.EMAIL_PROVIDER_DOMAIN',
      }
    }
  ];
}

  constructor(
    private leadService: LeadService,
    private toastr: ToastrService,
    private router: Router,
    private route: ActivatedRoute,
      private translate: TranslateService

  ) {}

  /* ================= INIT ================= */

  ngOnInit(): void {

      this.buildFields();

  this.translate.onLangChange.subscribe(() => {
    this.buildFields();
  });
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');

      if (id) {
        // ✅ EDIT — load from route
        this.leadSeq = +id;
        this.isEditMode = true;
        this.leadService.setLeadSeq(this.leadSeq);
        this.loadLeadById(this.leadSeq);
      } else {
        // ✅ NEW — clear everything, no fallback to cache
        this.leadSeq = null;
        this.isEditMode = false;

        this.leadService.setLeadSeq(null);
        this.leadService.setLeadForm(null); // ✅ clear cache immediately

        setTimeout(() => {
          this.dynamicForm?.form?.reset(); // ✅ reset form fields
        });
      }
    });

    this.route.queryParamMap.subscribe((q) => {
      this.isViewMode = q.get('mode') === 'view';
    });
  }

  ngAfterViewInit(): void {
    const saved = this.leadService.getLeadForm();

    // ✅ ONLY restore cache in edit mode AND id exists in route
    const routeId = this.route.snapshot.paramMap.get('id');

    if (saved && routeId && this.isEditMode && this.dynamicForm?.form) {
      setTimeout(() => {
        this.dynamicForm.form.patchValue(saved);
      });
    } else if (!routeId) {
      // ✅ extra safety — clear form if no id in route
      setTimeout(() => {
        this.dynamicForm?.form?.reset();
      });
    }
  }

saveAndContinue(): void {
  if (!this.leadSeq) {
    this.toastr.warning(this.translate.instant('DEMOGRAPHIC.PLEASE_SAVE_LEAD_FIRST'));
    return;
  }

  // ✅ Forward view mode to next page
  this.router.navigate(['/add-new-leads/document', this.leadSeq], {
    queryParams: this.isViewMode ? { mode: 'view' } : {},
  });
}

  /* ================= FORM ================= */

  onFormChange(value: any) {
    if (value) {
      const contactName = this.capitalizeWords(value.contactName);
      const contactNameCtrl = this.dynamicForm?.form?.get('contactName');

      if (contactNameCtrl && contactNameCtrl.value !== contactName) {
        contactNameCtrl.setValue(contactName, { emitEvent: false });
      }

      this.leadService.setLeadForm({ ...value, contactName });
    }
  }

  private capitalizeWords(value: any): any {
    if (typeof value !== 'string' || !value) {
      return value;
    }

    return value.replace(/(^|\s)(\S)/g, (_, space, char) => space + char.toUpperCase());
  }

  /* ================= SAVE & CONTINUE ================= */

  onFormSubmit(formValue: any): void {
    const payload = this.mapPayload(formValue);

    this.leadService.saveLead(payload).subscribe({
      next: (res: any) => {
        const id = res?.leadSeq;
        if (!id) {
          this.toastr.error(this.translate.instant('DEMOGRAPHIC.INVALID_LEAD_ID'));
          return;
        }

        this.leadSeq = id;
        this.isEditMode = true;
        this.leadService.setLeadSeq(id);

        const updatedForm = { ...formValue, leadId: id };
        this.dynamicForm.form.patchValue(updatedForm);
        this.leadService.setLeadForm(updatedForm);

        this.toastr.success(this.translate.instant('DEMOGRAPHIC.LEAD_SAVED', { id }));

        this.router.navigate(['/add-new-leads/demographic', id], { replaceUrl: true });

        // ✅ navigate to next if saveAndContinue was triggered
        if (this.continueAfterSave) {
          this.continueAfterSave = false;
          this.router.navigate(['/add-new-leads/document', id]);
        }
      },
      error: () => this.toastr.error(this.translate.instant('DEMOGRAPHIC.SAVE_FAILED')),
    });
  }

  submitForm(): void {
    if (this.isViewMode) return;
    this.dynamicForm.submit();
  }

  /* ================= LOAD ================= */

  loadLeadById(leadSeq: number): void {
    this.leadService.getLeadById(leadSeq).subscribe({
      next: (res: any) => {
        const mapped = this.mapResponseToForm(res);

        setTimeout(() => {
          this.dynamicForm.form.patchValue(mapped);
          this.leadService.setLeadForm(mapped);

          // ✅ disable AFTER patch
          if (this.isViewMode) {
            this.dynamicForm.form.disable();
          }
        });
      },
    });
  }

  mapResponseToForm(res: any): any {
    return {
      leadId: res?.leadSeq || null,
      clientName: res?.clntNm || '',
      contactName: res?.cntctNm || '',
      phone: res?.cntctNum || '',
      email: res?.cntctEmail || '',
      date: res?.leadCrtdDt ? new Date(res.leadCrtdDt).toISOString().split('T')[0] : '',
    };
  }

  mapPayload(form: any): any {
    return {
      leadSeq: this.leadSeq || null,
      clntNm: form.clientName,
      cntctNm: form.contactName,
      cntctNum: form.phone,
      cntctEmail: form.email,
      leadCrtdDt: form.date ? new Date(form.date).toISOString() : null,
      stsKey: 799,
      delFlg: 0,
    };
  }

  /* ================= NAV ================= */

  goNext(): void {
    if (!this.leadSeq) return;
    this.router.navigate(['/add-new-leads/document', this.leadSeq]);
  }
  /* ================= RESET ================= */

  resetLead(): void {
    this.leadSeq = null;
    this.isEditMode = false;

    this.leadService.setLeadSeq(null);
    this.leadService.setLeadForm(null);

    setTimeout(() => {
      this.dynamicForm?.form?.reset();
    });

    this.router.navigate(['/add-new-leads/demographic'], { replaceUrl: true });
    this.toastr.success(this.translate.instant('DEMOGRAPHIC.FORM_RESET'));
  }
  /* ================= MODAL ================= */

  openCreateFromEmail() {
    this.showCreateFromEmail = true;
  }

  closeCreateFromEmail() {
    this.showCreateFromEmail = false;
    this.selectedFile = null;
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.selectedFile = input.files[0];
    }
  }

  saveFile() {
    if (!this.selectedFile) return;

    const formData = new FormData();
    formData.append('file', this.selectedFile);
    // formData.append('leadSeq', this.leadSeq.toString());

    this.leadService.saveLeadDocs(formData).subscribe({
      next: (res: any) => {
        if (res?.error) {
          this.toastr.error(res.error);
          return;
        }

        this.toastr.success(this.translate.instant('COMMON.DOCUMENT_UPLOADED'));

        this.emailLeadExtraction();
        this.closeCreateFromEmail();
      },

      error: (err) => {
        this.toastr.error(this.translate.instant('DEMOGRAPHIC.UPLOAD_FAILED'));
      },
    });
  }

  emailLeadExtraction(): void {
    if (!this.emailText?.trim()) {
      this.toastr.error(this.translate.instant('DEMOGRAPHIC.EMAIL_TEXT_REQUIRED'));
      return;
    }

    this.leadService.emailLeadExtractionDirect(this.emailText).subscribe({
      next: (res: any) => {
        const mapped = {
          leadId: this.leadSeq,
          clientName: res?.clntNm || '',
          contactName: res?.cntctNm || '',
          email: res?.cntctEmail || '',
          phone: res?.cntctNum || '',
          date: res?.leadCrtdDt ? new Date(res.leadCrtdDt).toISOString().split('T')[0] : '',
        };

        this.dynamicForm.form.patchValue(mapped);
        this.leadService.setLeadForm(mapped);

        this.toastr.success(this.translate.instant('DEMOGRAPHIC.LEAD_EXTRACTED'));

        // ✅ ADD THIS
        this.closeCreateFromEmail();
      },
      error: () => {
        this.toastr.error(this.translate.instant('DEMOGRAPHIC.EXTRACTION_FAILED'));
      },
    });
  }
}
